import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Images } from "../../../utils/Images";
import { handleDonate } from "../../../utils/Donation";
import axiosInstance from "../../../api/config/axios";
import { INDIA_STATES } from "../../../utils/indiaStates";

const BRAND_RED = "#F04951";
const DARK_CHIP = "#3B3A3A";

// Colours a caller can override (the temple box passes its own); Life uses these defaults.
export const DEFAULT_DETAILS_THEME = {
  accent: BRAND_RED, // checkbox, selected dropdown item
  link: "#FF5858", // Terms of Use / Privacy Policy
  amountBg: "#F5F8ED",
  amountBorder: "#BDD188",
  amountTag: "#A9C74E", // "One time" / "Monthly" in the amount bar
  button: BRAND_RED,
  buttonRadius: { xs: "8px", sm: "11px" },
  buttonShadow: "0px 4px 0px #141414",
  buttonShadowActive: "0px 2px 0px #141414",
};

const paymentOptions = [
  { name: "PhonePe", src: Images.phonepayIcon },
  { name: "Gpay", src: Images.gpayIcon },
  { name: "Bhim UPI", src: Images.upiIcon },
  { name: "More", src: Images.moreIcon },
];

const inputSx = (hasError) => ({
  fontFamily: "Red Hat Display",
  fontWeight: 500,
  fontSize: { xs: "12px", sm: "14px" },
  color: "#141414",
  background: "#FFFFFF",
  border: `1px solid ${hasError ? BRAND_RED : "#DDE2E5"}`,
  borderRadius: { xs: "6px", sm: "9px" },
  height: { xs: "42px", sm: "50px" },
  px: "16px",
  width: "100%",
  outline: "none",
  boxSizing: "border-box",
  "&::placeholder": { color: "#979796" },
  "&:focus": { borderColor: hasError ? BRAND_RED : "#AAAAAA" },
});

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontFamily: "Red Hat Display",
      fontWeight: 600,
      fontSize: { xs: "13px", sm: "16px" },
      color: "#141414",
      mb: { xs: "7px", sm: "9px" },
    }}
  >
    {children}{" "}
    <Box component="span" sx={{ color: "#EC1B26" }}>*</Box>
  </Typography>
);

const SubscribeDetailsStep = ({
  amount,
  tipAmount = 0,
  currency,
  frequency,
  campaignId,
  campaignTitle,
  donationCategory = null,
  contributionOptions = [],
  contributionCategoryCodes = [],
  // Only the donate-once → monthly upsell needs a picker here; the Monthly tab already chose the
  // category in the box above, so repeating the dropdown there is redundant.
  showCategoryPicker = false,
  isPoolDonation = true,
  // Temple box only: a one-time gift recorded under this sub-category instead of a campaign.
  // Every other caller leaves it unset, so their one-time payload is unchanged.
  onceDonationCategory = null,
  // Display-only: which thank-you page to show after payment ("temple" = TempleThankYou)
  thankYouVariant = null,
  theme,
  // Temple box: an existing monthly donation for the category shows a toast (no
  // keep-or-replace dialog). Life leaves it off and keeps the dialog.
  existingSubscriptionToast = false,
  // Temple campaign page: the campaign the donor was on. Only the thank-you page reads it (to show
  // the temple thank-you page); it does not change where the money is credited.
  sourceCampaignId = null,
  // Temple box: called when the donor closes the Razorpay checkout without paying (incomplete
  // checkout) with what they were giving, so the page can offer to finish it or set a reminder.
  // When not passed (Life box), closing the checkout goes to the failure page as before.
  onCheckoutDismiss,
  onBack,
}) => {
  const t = { ...DEFAULT_DETAILS_THEME, ...(theme || {}) };
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // Monthly subscriptions choose a contribution category here (the once → upsell
  // flow never picked one). Initialize from the category passed in, if any.
  const initialCatIdx = Math.max(0, contributionCategoryCodes.indexOf(donationCategory));
  const [catIdx, setCatIdx] = useState(initialCatIdx);
  const [catMenuAnchor, setCatMenuAnchor] = useState(null);
  const hasCategories = contributionOptions.length > 0;
  const selectedCategory = contributionCategoryCodes[catIdx] || donationCategory;

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [billingState, setBillingState] = useState("");
  const [whatsappConsent, setWhatsappConsent] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Existing live subscription in the chosen category (keep-or-replace dialog)
  const [existingSub, setExistingSub] = useState(null);
  const [toastMessage, setToastMessage] = useState("");

  // Auto-fill from the logged-in user's profile (same behavior as DonateOncePage):
  // only fills fields the donor hasn't typed into yet
  useEffect(() => {
    if (!user || !user.id) return;
    // This form's phone input is 10 digits without +91 — normalize profile phone
    const profilePhone = String(user.phone || user.mobile || "")
      .replace(/\D/g, "")
      .slice(-10);
    setFullName((prev) => prev || user.name || "");
    setEmail((prev) => prev || user.email || "");
    setPhone((prev) => prev || (profilePhone.length === 10 ? profilePhone : ""));
    if (user.state && INDIA_STATES.includes(user.state)) {
      setBillingState((prev) => prev || user.state);
    }
  }, [user]);

  const isOnce = frequency === "once";
  const frequencyLabel = frequency === "quarterly" ? "Quarterly" : isOnce ? "One time" : "Monthly";

  const validate = () => {
    const errs = {};
    if (!fullName.trim()) errs.fullName = true;
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = true;
    if (phone.replace(/\D/g, "").length < 10) errs.phone = true;
    if (!billingState) errs.billingState = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const proceed = async () => {
    if (!validate()) return;
    const donorData = {
      fullName,
      email,
      phone: `+91${phone}`,
      billingState,
      whatsappConsent,
      amount,
      currency,
      frequency,
    };
    if (isOnce) {
      if (currency.code === "INR") {
        // Open Razorpay checkout directly — no redirect to the donate-once page.
        // isPoolDonation: "& Others" joins the pooled beneficiary program (backend credits
        // the top-priority life campaign still below goal); when false the donation is
        // credited directly to this campaign (campaignId).
        setSubmitting(true);
        try {
          const form = { name: fullName, email, phone: `+91${phone}` };
          const onceData = {
            currency: currency.code,
            state: billingState,
            whatsappUpdates: whatsappConsent,
            campaignId,
            campaignName: campaignTitle,
            tipAmount: tipAmount || 0,
            anonymousCheck: 0,
            isPoolDonation,
            ...(onceDonationCategory && { donationCategory: onceDonationCategory }),
            ...(sourceCampaignId && { sourceCampaignId }),
            ...(thankYouVariant && { thankYouVariant }),
          };
          await handleDonate(
            form,
            Number(amount),
            navigate,
            onceData,
            user?.id,
            null,
            onCheckoutDismiss
              ? { onDismiss: () => onCheckoutDismiss({ kind: "money", frequency: "once", amount: Number(amount), tipAmount: tipAmount || 0, form, additionalData: onceData }) }
              : undefined
          );
        } catch (error) {
          console.error("Donation error:", error);
          alert("An error occurred. Please try again.");
        } finally {
          setSubmitting(false);
        }
        return;
      }
      sessionStorage.setItem("returningFromCheckout", "true");
      navigate("/overseas-donor-page", {
        state: { campaignTitle, campaignId, amount, currency, isPoolDonation, ...donorData },
      });
    } else {
      // Monthly / quarterly → open Razorpay subscription checkout directly (no redirect).
      // Life-section monthly is a CATEGORY subscription (Critical care fundraisers / Rare condition fundraisers):
      // backend records every recurring charge per category, no campaign attribution.
      // Razorpay subscriptions are INR-only.
      if (currency.code !== "INR") {
        alert("Monthly donations are currently available in INR only. Please switch the currency to ₹ INR.");
        return;
      }
      // ONE live subscription per donor per category (Netflix-style): check first and
      // offer keep-or-replace instead of silently creating a duplicate.
      setSubmitting(true);
      if (selectedCategory) {
        try {
          const check = await axiosInstance.get("/payment/subscription/active-check", {
            params: { category: selectedCategory, email, ...(user?.id && { userId: user.id }) },
          });
          if (check?.exists) {
            setSubmitting(false);
            if (existingSubscriptionToast) {
              const sub = check.existing || {};
              const forLabel = contributionOptions[contributionCategoryCodes.indexOf(sub.donation_category)];
              setToastMessage(
                `You already have ${sub.status_label === "active" || !sub.status_label ? "an active" : `a ${sub.status_label}`} monthly donation` +
                  (sub.amount ? ` of ₹${Number(sub.amount).toLocaleString("en-IN")}/month` : "") +
                  (forLabel ? ` for ${forLabel}` : "") +
                  "."
              );
              return;
            }
            setExistingSub(check.existing);
            return; // the replace dialog takes over
          }
        } catch (_) {
          // pre-check unavailable → proceed; backend still enforces with 409
        }
      }
      await startMonthlySubscription(false);
    }
  };

  const startMonthlySubscription = async (replaceExisting) => {
    // TEST hook: open the page with ?test_billing=daily (or weekly) to create a
    // short-cycle subscription — backend honors it only on Razorpay TEST keys.
    const testBillingPeriod = new URLSearchParams(window.location.search).get("test_billing");
    setSubmitting(true);
    try {
      const form = { name: fullName, email, phone: `+91${phone}` };
      const monthlyData = {
        currency: currency.code,
        state: billingState,
        whatsappUpdates: whatsappConsent,
        campaignId: campaignId || "",
        campaignName: campaignTitle || "",
        donationType: "monthly",
        ...(selectedCategory && { donationCategory: selectedCategory }),
        ...(replaceExisting && { replaceExisting: true }),
        ...(testBillingPeriod && { testBillingPeriod }),
        ...(sourceCampaignId && { sourceCampaignId }),
        ...(thankYouVariant && { thankYouVariant }),
      };
      await handleDonate(
        form,
        Number(amount),
        navigate,
        monthlyData,
        user?.id,
        existingSubscriptionToast ? setToastMessage : null,
        onCheckoutDismiss
          ? {
              onDismiss: () => {
                // Don't carry replaceExisting into a retry from the reminder popup
                const { replaceExisting: _r, ...retryData } = monthlyData;
                onCheckoutDismiss({ kind: "money", frequency: "monthly", amount: Number(amount), tipAmount: 0, form, additionalData: retryData });
              },
            }
          : undefined
      );
    } catch (error) {
      console.error("Donation error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "26px" }}>
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontSize: { xs: "18px", sm: "24px" },
            color: "#000000",
            lineHeight: 1.2,
          }}
        >
          Enter your details
        </Typography>
        {/* Step back — returns to the previous step (upsell / amount) */}
        <Box
          component="button"
          onClick={onBack}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            appearance: "none",
            WebkitAppearance: "none",
            p: 0,
            flexShrink: 0,
            "&:hover": { opacity: 0.7 },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "16px", color: "#B9B9B8" }} />
          <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: "14px", color: "#B9B9B8" }}>
            Back
          </Typography>
        </Box>
      </Box>

      {/* ── "My contribution goes to" — monthly subscriptions choose a category
             here (Figma node 258:3430) ── */}
      {!isOnce && hasCategories && showCategoryPicker && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "6px", sm: "7px" }, mb: { xs: "16px", sm: "18px" } }}>
          <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: { xs: "13px", sm: "16px" }, color: "#141414", lineHeight: 1.4 }}>
            My contribution goes to:
          </Typography>
          <Box
            onClick={(e) => setCatMenuAnchor(e.currentTarget)}
            sx={{
              background: "#FFFFFF",
              border: "0.7px solid #B9B9B8",
              borderRadius: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "10px",
              py: "10px",
              cursor: "pointer",
              gap: "6px",
            }}
          >
            <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: { xs: "12px", sm: "14px" }, color: "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
              {contributionOptions[catIdx]}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: "16px", color: "#1D1D1B", flexShrink: 0 }} />
          </Box>
          <Menu
            anchorEl={catMenuAnchor}
            open={Boolean(catMenuAnchor)}
            onClose={() => setCatMenuAnchor(null)}
            PaperProps={{ sx: { borderRadius: "10px", width: catMenuAnchor?.offsetWidth, maxWidth: "none" } }}
          >
            {contributionOptions.map((option, idx) => (
              <MenuItem
                key={idx}
                selected={catIdx === idx}
                onClick={() => { setCatIdx(idx); setCatMenuAnchor(null); }}
                sx={{
                  fontFamily: "Red Hat Display",
                  fontSize: { xs: "12px", sm: "14px" },
                  minHeight: { xs: "auto", sm: "48px" },
                  py: { xs: "8px", sm: "6px" },
                  fontWeight: catIdx === idx ? 700 : 400,
                  color: catIdx === idx ? t.accent : "#1D1D1B",
                }}
              >
                {option}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      {/* ── Amount bar (read-only, same style as donation box) ── */}
      <Box
        sx={{
          background: t.amountBg,
          border: `1px solid ${t.amountBorder}`,
          borderRadius: "9px",
          px: "16px",
          height: { xs: "44px", sm: "54px" },
          display: "flex",
          alignItems: "center",
          gap: "10px",
          mb: { xs: "22px", sm: "28px" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "3px", flexShrink: 0 }}>
          <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: { xs: "14px", sm: "18px" }, color: "#4A4A49" }}>
            {currency.symbol}
          </Typography>
          <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: { xs: "14px", sm: "18px" }, color: "#4A4A49" }}>
            {currency.code}
          </Typography>
          <KeyboardArrowDownIcon sx={{ fontSize: { xs: "20px", sm: "22px" }, color: "#4A4A49" }} />
        </Box>
        <Box sx={{ width: "1px", height: "22px", background: "#B9B9B8", flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontSize: { xs: "14px", sm: "18px" },
            color: "#4A4A49",
            flex: 1,
          }}
        >
          {amount.toLocaleString()}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontSize: { xs: "14px", sm: "18px" },
            color: t.amountTag,
            flexShrink: 0,
          }}
        >
          {isOnce ? "One time" : frequencyLabel}
        </Typography>
      </Box>

      {/* ── Form grid ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: "16px", sm: "20px" }, mb: { xs: "24px", sm: "30px" } }}>
        {/* Full Name */}
        <Box>
          <FieldLabel>Full Name</FieldLabel>
          <Box
            component="input"
            type="text"
            placeholder="Enter Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={inputSx(errors.fullName)}
          />
        </Box>

        {/* Email ID */}
        <Box>
          <FieldLabel>Email ID</FieldLabel>
          <Box
            component="input"
            type="email"
            placeholder="Enter E mail Id"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={inputSx(errors.email)}
          />
        </Box>

        {/* Phone Number */}
        <Box>
          <FieldLabel>Phone Number</FieldLabel>
          <Box sx={{ display: "flex", gap: "9px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                border: `1px solid ${errors.phone ? BRAND_RED : "#DDE2E5"}`,
                borderRadius: { xs: "6px", sm: "7px" },
                px: "8px",
                height: { xs: "42px", sm: "50px" },
                background: "#FFFFFF",
                flexShrink: 0,
                cursor: "default",
              }}
            >
              <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, fontSize: { xs: "12px", sm: "14px" }, color: "#000000" }}>
                +91
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: "14px", color: "#000000" }} />
            </Box>
            <Box
              component="input"
              type="tel"
              placeholder="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              sx={{ ...inputSx(errors.phone), flex: 1, width: "auto" }}
            />
          </Box>
        </Box>

        {/* Billing State */}
        <Box>
          <FieldLabel>Billing State</FieldLabel>
          <Box sx={{ position: "relative" }}>
            <Box
              component="select"
              value={billingState}
              onChange={(e) => setBillingState(e.target.value)}
              sx={{
                fontFamily: "Red Hat Display",
                fontWeight: 500,
                fontSize: { xs: "12px", sm: "14px" },
                color: billingState ? "#141414" : "#979796",
                background: "#FFFFFF",
                border: `1px solid ${errors.billingState ? BRAND_RED : "#DDE2E5"}`,
                borderRadius: { xs: "6px", sm: "9px" },
                height: { xs: "42px", sm: "50px" },
                pl: "16px",
                pr: "32px",
                width: "100%",
                outline: "none",
                boxSizing: "border-box",
                cursor: "pointer",
                appearance: "none",
                WebkitAppearance: "none",
              }}
            >
              <option value="" disabled>Select State</option>
              {INDIA_STATES.map((s) => (
                <option key={s} value={s} style={{ color: "#1D1D1B" }}>
                  {s}
                </option>
              ))}
            </Box>
            <KeyboardArrowDownIcon
              sx={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "16px",
                color: "#1D1D1B",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* ── WhatsApp consent ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", mb: "8px" }}>
        <Checkbox
          checked={whatsappConsent}
          onChange={(e) => setWhatsappConsent(e.target.checked)}
          size="small"
          sx={{
            p: 0,
            mt: "-1px",
            color: t.accent,
            "&.Mui-checked": { color: t.accent },
          }}
        />
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 400,
            fontSize: { xs: "12px", sm: "14px" },
            color: "#1D1D1B",
            lineHeight: { xs: "16px", sm: "20px" },
          }}
        >
          I want to receive transaction and donation updates on WhatsApp
        </Typography>
      </Box>

      {/* ── Terms ── */}
      <Typography
        sx={{
          fontFamily: "Red Hat Display",
          fontWeight: 500,
          fontSize: { xs: "12px", sm: "14px" },
          color: "#1D1D1B",
          mb: { xs: "22px", sm: "30px" },
          lineHeight: { xs: "16px", sm: "20px" },
        }}
      >
        By continuing, you are agreeing to{" "}
        <Box
          component="span"
          sx={{ color: t.link, cursor: "pointer" }}
        >
          Terms of Use
        </Box>
        {" "}and{" "}
        <Box
          component="span"
          sx={{ color: t.link, cursor: "pointer" }}
        >
          Privacy Policy
        </Box>
      </Typography>

      {/* ── CTA button — "Donate" for once, "Subscribe" for recurring ── */}
      <Box
        component="button"
        onClick={submitting ? undefined : proceed}
        disabled={submitting}
        sx={{
          borderRadius: t.buttonRadius,
          width: "100%",
          height: { xs: "46px", sm: "58px" },
          border: "none",
          cursor: submitting ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          boxShadow: t.buttonShadow,
          appearance: "none",
          WebkitAppearance: "none",
          backgroundColor: t.button,
          opacity: submitting ? 0.75 : 1,
          mb: "16px",
          transition: "opacity 0.15s, transform 0.1s",
          "&:hover": { opacity: submitting ? 0.75 : 0.92 },
          "&:active": submitting ? {} : { transform: "translateY(2px)", boxShadow: t.buttonShadowActive },
        }}
      >
        {submitting && <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />}
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontSize: { xs: "14px", sm: "16px" },
            color: "#FFFFFF",
          }}
        >
          {submitting
            ? "Processing…"
            : isOnce
              ? `Donate ${currency.symbol}${amount.toLocaleString()}`
              : `Subscribe ${currency.symbol}${amount.toLocaleString()}/ ${frequencyLabel}`}
        </Typography>
      </Box>

      {/* ── Donate Via ── */}
      <Box>
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontSize: { xs: "14px", sm: "16px" },
            color: "#000000",
            mb: "7px",
          }}
        >
          Donate Via
        </Typography>
        <Box
          sx={{
            backgroundColor: DARK_CHIP,
            borderRadius: "15px",
            p: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {paymentOptions.map((option) => (
            <Box
              key={option.name}
              onClick={submitting ? undefined : proceed}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                width: "57px",
                "&:hover": { opacity: 0.8 },
              }}
            >
              <Box
                component="img"
                src={option.src}
                alt={option.name}
                sx={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
              />
              <Typography
                sx={{
                  fontFamily: "Red Hat Display",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#FFFFFF",
                  textAlign: "center",
                  lineHeight: "16.8px",
                }}
              >
                {option.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Snackbar open={Boolean(toastMessage)} autoHideDuration={5000} onClose={() => setToastMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} sx={{ zIndex: 1700 }}>
        <Alert onClose={() => setToastMessage("")} severity="warning" sx={{ width: "100%" }}>
          {toastMessage}
        </Alert>
      </Snackbar>

      {/* ── Keep-or-replace dialog: one live subscription per category ── */}
      <Dialog open={Boolean(existingSub)} onClose={() => setExistingSub(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "Red Hat Display", fontWeight: 700 }}>
          You already have a monthly donation
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Red Hat Display", fontSize: "14px", color: "#4A4A49", lineHeight: 1.7 }}>
            You have {existingSub?.status_label === "active" ? "an active" : `a ${existingSub?.status_label}`}{" "}
            monthly donation of <b>₹{Number(existingSub?.amount || 0).toLocaleString()}/month</b> for{" "}
            <b>
              {/* The dropdown's own label when it knows the code (e.g. a temple sub-category);
                  otherwise Life's two names, as before. */}
              {contributionOptions[contributionCategoryCodes.indexOf(existingSub?.donation_category)] ||
                (existingSub?.donation_category === "rare_diseases" ? "Rare condition fundraisers" : "Critical care fundraisers")}
            </b>.
            <br />
            <br />
            Do you want to replace it with <b>₹{Number(amount).toLocaleString()}/month</b>? Your existing
            subscription will be cancelled and no longer charged.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setExistingSub(null)}
            sx={{ fontFamily: "Red Hat Display", fontWeight: 600, color: "#686866", textTransform: "none" }}
          >
            Keep Existing
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setExistingSub(null);
              await startMonthlySubscription(true);
            }}
            sx={{
              fontFamily: "Red Hat Display",
              fontWeight: 700,
              textTransform: "none",
              background: t.button,
              "&:hover": { background: t.button, filter: "brightness(0.92)" },
            }}
          >
            Replace with ₹{Number(amount).toLocaleString()}/month
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscribeDetailsStep;
